import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Shield, Clock, AlertTriangle, CheckCircle, Loader2, FileSearch, Car, Wrench } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PAYER_LABELS = {
  customer: 'Customer',
  insurance: 'Insurance',
  warranty: 'Warranty',
  free: 'Free',
  unknown: '-',
};

const PAYER_STYLES = {
  customer: 'bg-blue-100 text-blue-800',
  insurance: 'bg-red-100 text-red-800',
  warranty: 'bg-purple-100 text-purple-800',
  free: 'bg-green-100 text-green-800',
  unknown: 'bg-gray-100 text-gray-600',
};

const TYPE_LABELS = {
  free_service: 'Free Service',
  paid_service: 'Paid Service',
  running_repair: 'Running Repair',
  accidental_repair: 'Accident Repair',
  campaign: 'Campaign',
  warranty: 'Warranty',
  key_readout: 'Key Read-out',
  other: 'Other',
};

function formatDate(dateStr) {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return dateStr; }
}

function formatAmount(amount) {
  if (amount == null) return '-';
  return `\u20B9${Number(amount).toLocaleString('en-IN')}`;
}

function formatKm(km) {
  if (km == null) return '-';
  return `${Number(km).toLocaleString('en-IN')} km`;
}

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (document.getElementById('razorpay-script')) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.id = 'razorpay-script';
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

/* ── Urgency-Driven Upsell CTA Card ── */
function ServiceHistoryUpsell({ inspectionId, price, onPaymentSuccess }) {
  const [loading, setLoading] = useState(false);

  const handlePurchase = async () => {
    setLoading(true);
    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        alert('Payment gateway failed to load. Please refresh and try again.');
        setLoading(false);
        return;
      }

      const { data } = await axios.post(`${API_URL}/api/inspections/${inspectionId}/service-history/create-order`);

      const options = {
        key: data.razorpay_key_id,
        amount: data.amount_paise,
        currency: data.currency,
        name: 'WiseDrive',
        description: 'Vehicle Service History Report',
        order_id: data.order_id,
        prefill: {
          name: data.customer_name || '',
          contact: data.customer_phone || '',
        },
        theme: { color: '#1e3a5f' },
        handler: async function (response) {
          try {
            await axios.post(`${API_URL}/api/inspections/${inspectionId}/service-history/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            onPaymentSuccess();
          } catch (err) {
            console.error('Payment verification failed:', err);
            alert('Payment received but verification failed. Please contact support.');
          }
        },
        modal: {
          ondismiss: () => setLoading(false),
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', () => {
        alert('Payment failed. Please try again.');
        setLoading(false);
      });
      rzp.open();
    } catch (err) {
      console.error('Failed to create order:', err);
      alert('Unable to initiate payment. Please try again.');
      setLoading(false);
    }
  };

  return (
    <section className="px-4 md:px-8 py-6" data-testid="service-history-upsell">
      <div className="max-w-7xl mx-auto">
        <div className="relative overflow-hidden rounded-2xl shadow-lg border border-slate-200"
          style={{background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #1e40af 100%)'}}>

          {/* Background pattern */}
          <div className="absolute inset-0 opacity-[0.07]"
            style={{backgroundImage: 'radial-gradient(circle at 25% 25%, white 1px, transparent 1px), radial-gradient(circle at 75% 75%, white 1px, transparent 1px)', backgroundSize: '24px 24px'}} />

          <div className="relative px-5 md:px-8 py-6 md:py-8">
            {/* Top urgency badge */}
            <div className="flex justify-center mb-4">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide bg-amber-400 text-amber-950">
                <Clock className="w-3 h-3" />
                Report delivered within 1 hour
              </span>
            </div>

            {/* Main content */}
            <div className="text-center mb-6">
              <h3 className="text-xl md:text-2xl font-bold text-white mb-2">
                Is this car hiding accidents or odometer fraud?
              </h3>
              <p className="text-sm text-blue-200 max-w-lg mx-auto">
                Don't risk buying a car with hidden damages. Get the complete authorized dealer service history
                and know the truth before you pay.
              </p>
            </div>

            {/* Feature pills */}
            <div className="flex flex-wrap justify-center gap-3 mb-6">
              {[
                { icon: AlertTriangle, label: 'Accident History', desc: 'Hidden collision repairs' },
                { icon: Car, label: 'Odometer Tampering', desc: 'KM rollback detection' },
                { icon: Shield, label: 'Insurance Claims', desc: 'Past claim records' },
                { icon: Wrench, label: 'Full Service Log', desc: 'Every dealer visit' },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-lg px-3 py-2 border border-white/10">
                  <item.icon className="w-4 h-4 text-amber-400 flex-shrink-0" />
                  <div>
                    <div className="text-xs font-semibold text-white leading-tight">{item.label}</div>
                    <div className="text-[10px] text-blue-300">{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <div className="flex flex-col items-center gap-2">
              <button
                onClick={handlePurchase}
                disabled={loading}
                data-testid="buy-service-history-btn"
                className="group inline-flex items-center gap-2.5 px-8 py-3.5 bg-amber-400 hover:bg-amber-300 active:bg-amber-500 text-slate-900 font-bold text-base rounded-xl shadow-xl shadow-amber-400/20 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <FileSearch className="w-5 h-5" />
                )}
                Get Service History — {'\u20B9'}{price}
              </button>
              <div className="flex items-center gap-3 mt-1">
                <span className="inline-flex items-center gap-1 text-[10px] text-blue-300">
                  <Shield className="w-3 h-3" /> Secure payment
                </span>
                <span className="text-blue-500">|</span>
                <span className="inline-flex items-center gap-1 text-[10px] text-blue-300">
                  <CheckCircle className="w-3 h-3" /> Verified dealer data
                </span>
                <span className="text-blue-500">|</span>
                <span className="inline-flex items-center gap-1 text-[10px] text-blue-300">
                  <Clock className="w-3 h-3" /> Within 60 mins
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Awaiting Status Card (Post-Payment) ── */
function ServiceHistoryAwaiting() {
  return (
    <section className="px-4 md:px-8 py-6" data-testid="service-history-awaiting">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
          {/* Header */}
          <div className="px-4 md:px-6 py-4 border-b bg-gradient-to-r from-slate-50 to-white">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">Authorized Service History</h3>
                <p className="text-xs text-gray-500">Payment confirmed — report is being prepared</p>
              </div>
            </div>
          </div>

          {/* Progress steps */}
          <div className="px-5 md:px-8 py-6">
            <div className="flex items-start gap-4">
              {/* Step indicators */}
              <div className="flex flex-col items-center gap-0">
                <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 text-white" />
                </div>
                <div className="w-0.5 h-8 bg-green-300" />
                <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center">
                  <Loader2 className="w-4 h-4 text-white animate-spin" />
                </div>
                <div className="w-0.5 h-8 bg-gray-200" />
                <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                  <FileSearch className="w-4 h-4 text-gray-400" />
                </div>
              </div>

              {/* Step labels */}
              <div className="flex flex-col gap-4 pt-1">
                <div>
                  <p className="text-sm font-semibold text-green-700">Payment Received</p>
                  <p className="text-xs text-gray-500">Your payment has been confirmed successfully</p>
                </div>
                <div className="pt-1">
                  <p className="text-sm font-semibold text-blue-700">Collecting Records</p>
                  <p className="text-xs text-gray-500">Our team is fetching the service history from authorized dealers</p>
                </div>
                <div className="pt-1">
                  <p className="text-sm font-medium text-gray-400">Report Ready</p>
                  <p className="text-xs text-gray-400">Complete service history will appear here shortly</p>
                </div>
              </div>
            </div>

            <div className="mt-5 bg-blue-50 rounded-lg p-3 border border-blue-100">
              <p className="text-xs text-blue-700 text-center">
                <Clock className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
                Estimated time: <strong>within 1 hour</strong>. Refresh this page to check for updates.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Main Component ── */
export function ServiceHistorySection({ inspectionId }) {
  const [records, setRecords] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasData, setHasData] = useState(false);
  const [dealerVerified, setDealerVerified] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState('none');
  const [price, setPrice] = useState(500);

  const fetchData = useCallback(() => {
    if (!inspectionId || inspectionId === 'demo' || inspectionId === 'sample') {
      setLoading(false);
      return;
    }

    Promise.all([
      axios.get(`${API_URL}/api/inspections/${inspectionId}/service-history/public`).catch(() => ({ data: { records: [], has_data: false } })),
      axios.get(`${API_URL}/api/inspections/${inspectionId}/service-history/payment-status`).catch(() => ({ data: { purchased: false, status: 'none' } })),
      axios.get(`${API_URL}/api/inspections/service-history-purchase/price`).catch(() => ({ data: { price: 500 } })),
    ]).then(([historyRes, paymentRes, priceRes]) => {
      setRecords(historyRes.data.records || []);
      setCampaigns(historyRes.data.campaigns || []);
      setSummary(historyRes.data.summary || null);
      setHasData(historyRes.data.has_data || false);
      setDealerVerified(historyRes.data.dealer_verified || false);
      setPaymentStatus(paymentRes.data.purchased ? 'paid' : paymentRes.data.status || 'none');
      setPrice(priceRes.data.price || 500);
    }).finally(() => setLoading(false));
  }, [inspectionId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return null;

  // Case 1: Service history data available — show the full table
  if (hasData && records.length > 0) {
    const hasAccidents = summary?.accident_count > 0;
    const hasInsurance = summary?.insurance_claims > 0;

    return (
      <section className="px-4 md:px-8 py-6" data-testid="report-service-history">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
            <div className="px-4 md:px-6 py-4 border-b bg-gradient-to-r from-slate-50 to-white">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">Authorized Service History</h3>
                    <p className="text-xs text-gray-500">
                      {summary?.key_readouts
                        ? `${summary.service_visits} dealer visits · ${summary.key_readouts} key read-outs`
                        : `${summary?.total_services} service records from authorized dealers`}
                    </p>
                  </div>
                </div>
                {(hasAccidents || hasInsurance || dealerVerified) && (
                  <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
                    {dealerVerified && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-700" data-testid="dealer-verified-badge">
                        <CheckCircle className="w-3 h-3" />
                        Dealer-Verified
                      </span>
                    )}
                    {hasAccidents && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium bg-red-100 text-red-700">
                        <AlertTriangle className="w-3 h-3" />
                        {summary.accident_count} Accident{summary.accident_count > 1 ? 's' : ''}
                      </span>
                    )}
                    {hasInsurance && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium bg-amber-100 text-amber-700">
                        {summary.insurance_claims} Insurance Claim{summary.insurance_claims > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {summary?.odometer_tampered && (
              <div className="mx-4 md:mx-6 my-3 bg-red-100 border-2 border-red-400 rounded-lg p-3" data-testid="report-odometer-tamper">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-bold text-red-800">Odometer Tampering Detected</h4>
                    <p className="text-xs text-red-700 mt-1">
                      Current odometer (<strong>{summary.current_odometer ? Number(summary.current_odometer).toLocaleString('en-IN') : '?'} km</strong>) is lower than
                      last service history record (<strong>{summary.last_service_km ? Number(summary.last_service_km).toLocaleString('en-IN') : '?'} km</strong>).
                      KM may have been reversed.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {campaigns.length > 0 && (
              <div className="mx-4 md:mx-6 my-3 border rounded-lg p-3 bg-violet-50/50 border-violet-200" data-testid="report-sh-campaigns">
                <h4 className="text-sm font-semibold text-gray-900 mb-1.5">Manufacturer Campaigns &amp; Recalls</h4>
                <ul className="space-y-1">
                  {campaigns.map((c, i) => {
                    const isOpen = String(c.status || '').toLowerCase().startsWith('open');
                    return (
                      <li key={c.number || i} className="text-xs text-gray-700 flex items-start gap-2">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${isOpen ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                          {c.status || '—'}
                        </span>
                        <span><strong>{c.number || '—'}</strong> — {c.description || '—'}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full text-sm" style={{ minWidth: '500px' }}>
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 w-[100px]">Date</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Service Type</th>
                    <th className="text-right px-3 py-2 text-xs font-medium text-gray-500 w-[100px]">KM Reading</th>
                    <th className="text-right px-3 py-2 text-xs font-medium text-gray-500 w-[80px]">Amount</th>
                    <th className="text-center px-3 py-2 text-xs font-medium text-gray-500 w-[80px]">Payer</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((record, idx) => (
                    <tr key={record.id || idx} className={`border-b ${record.is_accident_related ? 'bg-red-50/40' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                      <td className="px-4 py-2.5 text-xs font-medium text-gray-900 whitespace-nowrap">{formatDate(record.date)}</td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-xs text-gray-700">{TYPE_LABELS[record.service_type] || record.service_type}</span>
                          {record.is_accident_related && (
                            <span className="text-[9px] font-semibold text-red-600 bg-red-100 px-1 py-0.5 rounded leading-none">ACCIDENT</span>
                          )}
                        </div>
                        {record.services && (
                          <p className="text-[10px] text-gray-500 mt-0.5 truncate max-w-[300px]">
                            {Array.isArray(record.services)
                              ? record.services
                                  .map(s => (typeof s === 'object' && s !== null ? (s.name || s.service || '') : String(s)))
                                  .filter(Boolean)
                                  .join(', ')
                              : record.services}
                          </p>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right text-xs text-gray-900 whitespace-nowrap">{formatKm(record.km_reading)}</td>
                      <td className="px-3 py-2.5 text-right text-xs font-medium text-gray-900 whitespace-nowrap">{formatAmount(record.invoice_amount)}</td>
                      <td className="px-3 py-2.5 text-center">
                        <span className={`inline-block px-1.5 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${PAYER_STYLES[record.payer] || PAYER_STYLES.unknown}`}>
                          {PAYER_LABELS[record.payer] || record.payer}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="px-4 py-3 bg-gray-50 border-t">
              <p className="text-[10px] text-gray-400 text-center">
                Service history sourced from authorized dealer records. Data accuracy depends on dealer documentation.
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Case 2: Payment done but service history not yet uploaded
  if (paymentStatus === 'paid') {
    return <ServiceHistoryAwaiting />;
  }

  // Case 3: No service history, no payment — show upsell CTA
  return (
    <ServiceHistoryUpsell
      inspectionId={inspectionId}
      price={price}
      onPaymentSuccess={() => setPaymentStatus('paid')}
    />
  );
}
